import {
  type TireInventoryResponse,
  type TireInventoryQueryParams,
  type CreateTireInventoryRequest,
  type TireInventory,
  type InstallTireRequest,
} from 'types/tire-inventory.types';
import {
  type TireTimelineRequest,
  type TireTimelineResponse,
  type TireRecommendationResponse,
} from 'types/tire-timeline.types';
import BaseAPI from './base.api';

export default class TireInventoryAPI extends BaseAPI {
  /**
   * Gets tire inventory with pagination
   * @param params Query parameters for filtering and pagination
   * @returns Tire inventory list with pagination details
   */
  getTireInventory = async (
    params: TireInventoryQueryParams = {}
  ): Promise<TireInventoryResponse> => {
    const response = await this.API.get('tire-inventory', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 10,
        ...(params.customerId && { customerId: params.customerId }),
        ...(params.keyword && { keyword: params.keyword }),
      },
    });
    return response.data;
  };

  /**
   * Creates a new tire inventory entry
   * @param payload Tire inventory data
   * @param customerId Customer ID
   * @returns Created tire inventory
   */
  createTireInventory = async (
    payload: CreateTireInventoryRequest,
    customerId: string
  ): Promise<TireInventory> => {
    const response = await this.API.post('tire-inventory', payload, {
      params: {
        customerId,
      },
    });
    return response.data;
  };

  /**
   * Installs a tire to an asset
   * @param payload Tire installation data
   * @param customerId Customer ID
   * @returns Installation log
   */
  installTire = async (
    payload: InstallTireRequest,
    customerId: string
  ): Promise<any> => {
    const response = await this.API.post('tire-installation-log', payload, {
      params: {
        customerId,
      },
    });
    return response.data;
  };

  /**
   * Gets tire timeline events
   * @param customerId Customer ID (query param)
   * @param payload Body (serialNumber or assetId+position, filters)
   * @returns Timeline events with telemetry
   */
  getTimeline = async (
    customerId: string,
    payload: TireTimelineRequest
  ): Promise<TireTimelineResponse> => {
    const response = await this.axiosInstance.post(
      'tire-inventory/timeline',
      payload,
      { params: { customerId } }
    );
    return response.data;
  };

  /**
   * Exports tire timeline events as PDF
   * @param customerId Customer ID (query param)
   * @param payload Body (serialNumber or assetId+position, filters)
   * @returns PDF Blob
   */
  exportTimelinePdf = async (
    customerId: string,
    payload: TireTimelineRequest
  ): Promise<Blob> => {
    const response = await this.axiosInstance.post(
      'tire-inventory/timeline/pdf',
      payload,
      {
        params: { customerId },
        responseType: 'blob',
      }
    );
    return response.data;
  };

  /**
   * Gets AI tire recommendation for an installed tire
   */
  getTireRecommendation = async (
    customerId: string,
    assetId: string,
    tirePosition: string
  ): Promise<TireRecommendationResponse> => {
    const response = await this.API.get('tire-recommendation/recommendation', {
      params: { customerId, assetId, tirePosition },
    });
    return response.data;
  };

  /**
   * Bulk imports tire inventory from Excel/CSV file
   */
  importTireInventory = async (
    file: File,
    customerId: string
  ): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.API.post('tire-inventory/import', formData, {
      params: { customerId },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  };
}
